import App from 'antd/lib/app';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { QueryKey } from '../../query-client';
import {
    fetchCustomerUtilityBills,
    fetchCustomerUtilityBillById,
    fetchCustomerUtilityBillUtilities,
    exportCustomerUtilityBillsToXlsx,
    updateCustomerUtilityBill,
    verifyCustomerUtilityBills,
    unverifyCustomerUtilityBills,
    reparseCustomerUtilityBills,
    IBatchVerifyResult,
    IBatchReparseResult,
    ICustomerUtilityBillsQuery,
    IUpdateCustomerUtilityBill,
} from './index';
import { ICustomerUtilityBill } from './interface';
import { IPagination } from '../IPagination';

export const useCustomerUtilityBillsQuery = (query: ICustomerUtilityBillsQuery, options = {}) => {
    return useQuery<IPagination<ICustomerUtilityBill>, Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_REPORTS, query],
        queryFn: ({ signal }) => fetchCustomerUtilityBills(query, signal),
        keepPreviousData: true,
        ...options,
    });
};

// Fetches a single bill by id. Used with `refetchInterval` to poll live re-parse status from the edit modal —
// the caller passes `enabled`/`refetchInterval` via `options` to start/stop polling based on the last result.
export const useCustomerUtilityBillQuery = (id: string, options = {}) => {
    return useQuery<ICustomerUtilityBill, Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_DETAIL, id],
        queryFn: ({ signal }) => fetchCustomerUtilityBillById(id, signal),
        ...options,
    });
};

// Downloads the bills list as an XLSX file for the given query and (optional) column keys. Kept as a mutation so the button
// can show a loading state and surface errors via a notification; success is signalled by the browser save.
export const useExportCustomerUtilityBillsMutation = () => {
    const { notification } = App.useApp();

    return useMutation<void, Error, { query: ICustomerUtilityBillsQuery; fields?: string[] }>(
        ({ query, fields }) => exportCustomerUtilityBillsToXlsx(query, fields),
        {
            onError: (error: Error) => notification.error({ message: error.message || 'Couldn’t export the bills' }),
        }
    );
};

export const useCustomerUtilityBillUtilitiesQuery = (options = {}) => {
    return useQuery<string[], Error>({
        queryKey: [QueryKey.CUSTOMER_BILL_UTILITIES],
        queryFn: ({ signal }) => fetchCustomerUtilityBillUtilities(signal),
        ...options,
    });
};

// Success messaging is left to the caller (it has the row/selection context); the hook only handles
// list invalidation and error surfacing, which are identical for every caller.
export const useUpdateCustomerUtilityBillMutation = () => {
    const { notification } = App.useApp();
    const queryClient = useQueryClient();

    return useMutation<ICustomerUtilityBill, Error, { id: string; body: IUpdateCustomerUtilityBill }>(
        ({ id, body }) => updateCustomerUtilityBill(id, body),
        {
            onSuccess: () => queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_REPORTS]),
            onError: (error: Error) => notification.error({ message: error.message || 'Couldn’t update the bill' }),
        }
    );
};

// Snapshot of every cached bills-list page, captured before an optimistic write so we can roll back on error.
type BillListSnapshot = [readonly unknown[], IPagination<ICustomerUtilityBill> | undefined][];

// Verify / un-verify optimistically flip `verifiedAt` on the affected rows across every cached list page, so the
// toggle feels instant; the real values are reconciled by the refetch in onSettled (and rolled back on error).
const useSetVerificationMutation = (verify: boolean) => {
    const { notification } = App.useApp();
    const queryClient = useQueryClient();

    const mutationFn = verify ? verifyCustomerUtilityBills : unverifyCustomerUtilityBills;
    const verb = verify ? 'verify' : 'unverify';

    return useMutation<IBatchVerifyResult, Error, string[], { snapshot: BillListSnapshot }>(mutationFn, {
        onMutate: async (ids: string[]) => {
            await queryClient.cancelQueries([QueryKey.CUSTOMER_BILL_REPORTS]);

            const snapshot = queryClient.getQueriesData<IPagination<ICustomerUtilityBill>>([QueryKey.CUSTOMER_BILL_REPORTS]);
            const verifiedAt = verify ? new Date().toISOString() : null;

            queryClient.setQueriesData<IPagination<ICustomerUtilityBill>>([QueryKey.CUSTOMER_BILL_REPORTS], old =>
                old ? { ...old, data: old.data.map(bill => (ids.includes(bill.id) ? { ...bill, verifiedAt } : bill)) } : old
            );

            return { snapshot };
        },
        onError: (error, _ids, context) => {
            context?.snapshot.forEach(([key, data]) => queryClient.setQueryData(key, data));
            notification.error({ message: error.message || `Couldn’t ${verb} the selected bills` });
        },
        onSettled: () => queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_REPORTS]),
    });
};

export const useVerifyCustomerUtilityBillsMutation = () => useSetVerificationMutation(true);

export const useUnverifyCustomerUtilityBillsMutation = () => useSetVerificationMutation(false);

export const useReparseCustomerUtilityBillsMutation = () => {
    const { notification } = App.useApp();
    const queryClient = useQueryClient();

    return useMutation<IBatchReparseResult, Error, string[]>(reparseCustomerUtilityBills, {
        onSuccess: () => queryClient.invalidateQueries([QueryKey.CUSTOMER_BILL_REPORTS]),
        onError: (error: Error) => notification.error({ message: error.message || 'Couldn’t re-parse the selected bills' }),
    });
};
